import { ReactNode } from 'react'
import { ProposalIconType } from '../../../types/type';
import { ConflictRequest } from '../../../api/gms/proposal/proposal-api-state';
import TableLoading from '../../ui/table-loading';
import TableError from '../../ui/table-error';
import TableEmpty from '../../ui/table-empty';
import { proposalHeaderData } from '../../../constant/gms-constant';
import { ActionTypeConflictProposal } from '../../../api/gms/proposal/proposal-api';
import { useAuthDataState } from '../../../hooks/data-state/use-auth-data-state';

interface Props {
    typeOfIcon: ProposalIconType;
    isLoadingGetProposal: boolean;
    isErrorGetProposal: boolean;
    errorMessage: string;
    spinnerColor?: string;
    spinnerSize?: number;
    getProposal: ConflictRequest[] | undefined;
    handleEdit?: (value: number, userID: number, status: string) => void;
    handleGenerate?: (value: number, userID: number, status: string) => void;
    handleApprove?: (value: number, userID: number, status: string, type: ActionTypeConflictProposal) => void;
    handleReject?: (value: number, userID: number, status: string, type: ActionTypeConflictProposal) => void;
    handleView?: (value: number, userID: number, status: string, type: ActionTypeConflictProposal | null) => void;
    headers?: string[];
    disableRowClick?: boolean;
}

export default function ConflictProposalForApprovalListTBody({
    headers = [],
    typeOfIcon,
    isLoadingGetProposal,
    isErrorGetProposal,
    errorMessage,
    spinnerColor = '#186cb2',
    spinnerSize = 25,
    getProposal,
    handleEdit,
    handleGenerate,
    handleApprove,
    handleReject,
    handleView,
}: Props) {
    const { isUserMonitoring } = useAuthDataState();
    const secondaryButton = `inline-flex w-20 cursor-pointer items-center justify-center truncate rounded-xl border border-[#186cb2] bg-gray-50 px-2.5 py-1 text-[10px] font-semibold capitalize text-[#186cb2] transition-colors hover:bg-[#186cb2] hover:text-white sm:text-[11px]`;

    const renderColumn = (header: string, proposal: ConflictRequest): ReactNode => {
        switch (header) {
            case proposalHeaderData.groupName.label:
                return (
                    <div
                        className="truncate font-medium text-gray-800"
                        title={proposal?.company_name ?? '-'}
                    >
                        {proposal?.company_name ?? '-'}
                    </div>
                );

            case proposalHeaderData.proposalType.label:
                return (
                    <div
                        className="truncate text-gray-700"
                        title={proposal?.new_product_type ?? '-'}
                    >
                        {proposal?.new_product_type ?? '-'}
                    </div>
                );

            case proposalHeaderData.product.label:
                return (
                    <div
                        className="truncate text-gray-700"
                        title={proposal?.plan_name ?? '-'}
                    >
                        {proposal?.plan_name ?? '-'}
                    </div>
                );

            case proposalHeaderData.basicPlan.label:
                return (
                    <div
                        className="truncate text-gray-700"
                        title={proposal?.plan_name ?? '-'}
                    >
                        {proposal?.plan_name ?? '-'}
                    </div>
                );

            case proposalHeaderData.preparedBy.label:
                return (
                    <div
                        className="truncate text-gray-600"
                        title={proposal?.requested_by_name ?? '-'}
                    >
                        {proposal?.requested_by_name ?? '-'}
                    </div>
                );

            case proposalHeaderData.conflictProposalAction.label: {
                const status = proposal?.status ?? '-';

                return (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                if(isUserMonitoring) return;
                                handleReject?.(
                                    proposal.conflict_id,
                                    proposal.existing_owner_id,
                                    proposal.status ?? "-",
                                    'reject-conflict'
                                );
                            }}
                            className="inline-flex w-20 cursor-pointer items-center justify-center truncate rounded-xl border border-red-300 bg-red-50 px-2.5 py-1 text-[10px] font-semibold capitalize text-red-600 transition-colors hover:bg-red-600 hover:text-white sm:text-[11px]"
                        >
                            Reject
                        </button>

                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                if(isUserMonitoring) return;
                                handleApprove?.(
                                    proposal.conflict_id,
                                    proposal.existing_owner_id,
                                    proposal.status ?? "-",
                                    'approve-conflict'
                                );
                            }}
                            className="inline-flex w-20 cursor-pointer items-center justify-center truncate rounded-xl border border-green-300 bg-green-50 px-2.5 py-1 text-[10px] font-semibold capitalize text-green-600 transition-colors hover:bg-green-600 hover:text-white sm:text-[11px]"
                        >
                            Approve
                        </button>

                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                if(isUserMonitoring) return;
                                handleView?.(
                                    proposal.application_id,
                                    proposal.existing_owner_id,
                                    proposal.status ?? "-",
                                    null
                                );
                            }}
                            className={secondaryButton}
                        >
                            View
                        </button>
                    </div>
                );
            }

            default:
                return '-';
        }
    };

    const getCellClassName = (header: string) => {
        if (header === proposalHeaderData.conflictProposalAction.label) {
            return 'px-3 py-3 text-center sm:px-4';
        }

        return 'px-3 py-3 text-gray-700 sm:px-4';
    };

    return (
        <tbody className="divide-y divide-gray-100">
            {isLoadingGetProposal ? (
                <TableLoading />
            ) : isErrorGetProposal ? (
                <TableError description={errorMessage} />
            ) : !getProposal || getProposal.length === 0 ? (
                <TableEmpty />
            ) : (
                getProposal.map((proposal, index) => (
                    <tr
                        key={proposal.conflict_id}
                        className={`border-b border-gray-100 text-xs transition-colors duration-150 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'
                        }`}
                    >
                        {headers.map((header) => (
                            <td
                                key={header}
                                className={getCellClassName(header)}
                            >
                                {renderColumn(header, proposal)}
                            </td>
                        ))}
                    </tr>
                ))
            )}
        </tbody>
    );
}